import { inject, Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { of, shareReplay } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  ByPatternPolicyDto,
  ByValuePolicyDto,
  PasswordPoliciesDto,
  PasswordPolicyDto,
  PolicyTypes,
} from '../models/PasswordPoliciesDtoModel';
import {
  ByPatternPolicy,
  ByValuePolicy,
  ByValueRules,
  PasswordPolicies,
  PasswordPolicy,
} from '../models/PasswordPoliciesInternalModel';
import { ApiService, LogService } from '../providers';
import { getPasswordPolicyTranslationKey } from '../utils/password-policy-utils';

@Injectable({
  providedIn: 'root',
})
export class PasswordValidationService {
  private readonly api = inject(ApiService);
  private static readonly PASSWORD_POLICIES_URL = 'data/passwordoptions';

  /**
   * Ein Observable, das die Passwortrichtlinien vom Backend lädt und speichert.
   *
   * Die Daten werden von den DTOs der API in saubere, interne Modelle umgewandelt.
   * Fehler beim Laden werden in der Konsole ausgegeben und das Observable gibt `null` aus.
   * Der `shareReplay(1)`-Operator stellt sicher, dass die Daten nur einmal geladen und
   * an alle Abonnenten weitergegeben werden.
   */
  public policies$ = this.api.restGet<PasswordPoliciesDto>(PasswordValidationService.PASSWORD_POLICIES_URL).pipe(
    map((policiesDto) =>
      policiesDto.passwordPolicies?.length > 0
        ? this.mapPolicies(policiesDto)
        : { ...policiesDto, passwordPolicies: [] },
    ),
    catchError((error) => {
      LogService.error(error.stack ?? error.message, true, 'Fehler beim Laden der Passwortrichtlinien:');
      return of({ passwordPolicies: [] });
    }),
    shareReplay(1),
  );

  /**
   * Erstellt eine Angular-Validatorfunktion, die ein Passwort gegen eine Liste dynamisch geladener
   * {@link PasswordPolicy}-Regeln prüft.
   *
   * Die Funktion unterstützt sowohl Pattern-basierte Regeln (`ByPattern`), bei denen ein regulärer Ausdruck
   * auf das Passwort angewendet wird, als auch wertbasierte Regeln (`ByValue`) wie z.B Mindestlänge.
   *
   * Bei Verstößen werden die Namen der verletzten Regeln (z.B als Übersetzungs-Keys) in der Fehlerstruktur
   * unter `passwordPolicy.toValidateRules` zurückgegeben.
   *
   * @param policies Eine Liste von Passwortregeln, die zur Validierung herangezogen werden
   * @returns Ein `ValidatorFn`, das in Angular-FormControls verwendet werden kann
   * @throws {Error} Wenn keine Regeln übergeben wurden
   */
  public createPasswordValidator(policies: PasswordPolicy[]): ValidatorFn {
    if (policies.length === 0) {
      throw new Error('Ungueltiger Passwortregeln vom Backend.');
    }

    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value;
      if (value === null || value === '') {
        return null;
      }

      const toValidateRules: string[] = [];
      for (const policy of policies) {
        if (policy.type === PolicyTypes.ByPattern) {
          if (this.violatesPatternPolicy(policy, value)) {
            toValidateRules.push(getPasswordPolicyTranslationKey(policy.name));
          }
        } else if (policy.type === PolicyTypes.ByValue) {
          if (this.violatesValuePolicy(policy as ByValuePolicy, value)) {
            toValidateRules.push(getPasswordPolicyTranslationKey(policy.name));
          }
        } else {
          console.warn('Unbekannte Regel ignoriert:', policy);
        }
      }

      return toValidateRules.length ? { passwordPolicy: { toValidateRules } } : null;
    };
  }

  /**
   * Prüft, ob ein Passwort eine ByPattern-Regel verletzt.
   *
   * @param policy Die ByPattern-Regel, die geprüft werden soll.
   * @param value Das zu prüfende Passwort als String.
   * @returns `true`, wenn die Regel verletzt wird, sonst `false`.
   */
  private violatesPatternPolicy(policy: ByPatternPolicy, value: string): boolean {
    return !policy.pattern.test(value);
  }

  /**
   * Prüft, ob ein Passwort eine ByValue-Regel verletzt.
   *
   * Diese Methode validiert Regeln wie Mindest- oder Maximallänge.
   *
   * @param policy Die ByValue-Regel, die geprüft werden soll.
   * @param value Das zu prüfende Passwort als String.
   * @returns `true`, wenn die Regel verletzt wird, sonst `false`.
   */
  private violatesValuePolicy(policy: ByValuePolicy, value: string): boolean {
    if (policy.name === ByValueRules.MinimumLength) {
      return value.length < policy.value;
    }
    if (policy.name === ByValueRules.MaximumLength) {
      return value.length > policy.value;
    }
    return false;
  }

  /**
   * Wandelt ein vom Backend geliefertes {@link PasswordPoliciesDto}-Objekt
   * in ein neues Objekt mit vollständig typisierten und vorbereiteten Passwortregeln um.
   *
   * - Bei `ByPattern`-Regeln wird der reguläre Ausdruck-String in ein `RegExp`-Objekt konvertiert.
   * - `ByValue`-Regeln werden unverändert übernommen.
   *
   * @param data Das vom Backend gelieferte {@link PasswordPoliciesDto}-Objekt.
   * @returns Ein neues {@link PasswordPolicies}-Objekt mit verarbeiteten Regeln.
   * @throws {Error} Wenn ein unbekannter Regeltyp gefunden wird.
   */
  private mapPolicies(data: PasswordPoliciesDto): PasswordPolicies {
    const mappedPolicies: PasswordPolicy[] = data.passwordPolicies.map((policyDto) => this.mapSinglePolicy(policyDto));

    return {
      passwordPolicies: mappedPolicies,
    };
  }

  /**
   * Führt die Umwandlung einer einzelnen Regel von einem DTO in das interne Modell durch.
   *
   * @param policyDto Das DTO der einzelnen Regel von der API.
   * @returns Das umgewandelte interne Regelmodell.
   * @throws {Error} Wenn ein unbekannter Regeltyp gefunden wird.
   */
  private mapSinglePolicy(policyDto: PasswordPolicyDto): PasswordPolicy {
    if (policyDto.$type === PolicyTypes.ByPattern) {
      const byPatternDto = policyDto as ByPatternPolicyDto;
      return {
        name: byPatternDto.name,
        type: PolicyTypes.ByPattern,
        pattern: this.toRegExp(byPatternDto.pattern),
      } as ByPatternPolicy;
    }

    if (policyDto.$type === PolicyTypes.ByValue) {
      const byValueDto = policyDto as ByValuePolicyDto;
      return {
        name: byValueDto.name,
        type: PolicyTypes.ByValue,
        value: byValueDto.value,
      } as ByValuePolicy;
    }

    throw new Error(`Unbekannter Policy-Typ: ${JSON.stringify(policyDto)}`);
  }

  /**
   * Wandelt einen Pattern-String sicher in ein RegExp-Objekt um.
   *
   * @param pattern Der Pattern-Wert als String.
   * @returns Ein gültiges RegExp-Objekt.
   */
  private toRegExp(pattern: string): RegExp {
    try {
      return new RegExp(pattern);
    } catch (error) {
      console.error(`Ungueltiger pattern vom Backend.: ${pattern}`, error);
      return new RegExp('.^');
    }
  }
}
